import { useSuspenseQuery } from "@tanstack/react-query";
import { parseISO } from "date-fns";

import { ChartLegend } from "@/components/Charts/ChartLegend";
import { DonutChart } from "@/components/Charts/DonutChart";
import { dataColors } from "@/components/Charts/theme";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { NoData } from "@/components/NoData/NoData";
import { Stat } from "@/components/Stat/Stat";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { useSlug } from "@/hooks/useSlug";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

const CHART_SIZE = 220;

export function EngagementCard(): React.ReactNode {
  const slug = useSlug();
  const { t, i18n } = useTranslation();

  const analyticsQueries = useAnalyticsQueries();
  const { data } = useSuspenseQuery(analyticsQueries.engagement());

  const engagements = data.engagements;

  const lastMonthEngagement = engagements.find((x) => x.type === "lastMonth");
  const last7DaysEngagement = engagements.find((x) => x.type === "last7Days");
  const last24HoursEngagement = engagements.find((x) => x.type === "last24Hours");

  const noData = !data || !lastMonthEngagement || !last7DaysEngagement || !last24HoursEngagement;

  return (
    <AnalyticsCard.Root>
      <AnalyticsCard.Header
        title={t("component.analytics-widget.engagement.title")}
        tooltip={t("page.analytics-overview.chart.resident-engagement.title-tooltip")}
      >
        <Button variant="ghostPrimary" type="link" href={routes.analytics.engagementDetails({ slug })}>
          {t("common.action.see-more")}
        </Button>
      </AnalyticsCard.Header>
      {noData ? (
        <NoData />
      ) : (
        <Inline gap="lg" align="center" justify="between" collapseBelow="xl" wrap={false}>
          <Stack align="center" gap="md">
            <DonutChart size={CHART_SIZE} value={lastMonthEngagement?.engagementPercentage ?? 0} />
            <ChartLegend
              items={[
                {
                  label: t("page.analytics-overview.chart.resident-engagement.chart-legend"),
                  color: dataColors.primary,
                },
              ]}
            />
            {lastMonthEngagement && (
              <span className="text-overline text-text-secondary italic">
                {formatDate(i18n, "dateMonthYearShort", parseISO(lastMonthEngagement.from))} -{" "}
                {formatDate(i18n, "dateMonthYearShort", parseISO(lastMonthEngagement.to))}
              </span>
            )}
          </Stack>
          {(last24HoursEngagement || last7DaysEngagement) && (
            <section className="grid w-full grow grid-cols-2 gap-space-md">
              {last24HoursEngagement && (
                <Stat
                  label={t("page.analytics-overview.chart.resident-engagement.card.day")}
                  value={last24HoursEngagement.engagementPercentage}
                  renderValue={(value) => `${value}%`}
                />
              )}
              {last7DaysEngagement && (
                <Stat
                  label={t("page.analytics-overview.chart.resident-engagement.card.week")}
                  value={last7DaysEngagement.engagementPercentage}
                  renderValue={(value) => `${value}%`}
                />
              )}
            </section>
          )}
        </Inline>
      )}
    </AnalyticsCard.Root>
  );
}
